import type { PlainDate } from "@sa-nu/shared-utils/date";
import { useEffect, useRef } from "react";

import { useGtm } from "../../../../lib/google-tag-manager/gtm";

/**
 * 部屋タイプ予約のフィルタ変更を GTM に送る。
 * 各フィルタの確定値（nuqs URL 状態）の変化を監視し、変わったときにイベントを送る。
 * 初回マウント（URL の初期値）では送らない。ポップアップ決定・チップ削除・クリアの
 * どの経路で変わっても、確定値が変われば 1 回だけ送られる。
 */
export function useRoomTypeFilterTracking({
  areaSlugs,
  firstNight,
  lastNight,
  facilitySlugs,
  evChargerAvailable,
  architectureTypes,
  largeOnly,
}: {
  areaSlugs: ReadonlySet<string>;
  firstNight: PlainDate | null;
  lastNight: PlainDate | null;
  facilitySlugs: ReadonlySet<string>;
  evChargerAvailable: boolean;
  architectureTypes: ReadonlySet<string>;
  largeOnly: boolean;
}) {
  const gtm = useGtm();

  // reservation-form(filter-modal) の handleConfirm に合わせ、値は送らず、
  // 絞り込みが有効(非空)なときだけ送る。clear で空になった場合は送らない。
  const areaKey = [...areaSlugs].sort().join(",");
  useChangeEffect(() => {
    if (areaSlugs.size === 0) return;
    gtm.sendEvent("rt_rsv_area_filtered");
  }, areaKey);

  const dateKey = `${firstNight?.toString() ?? ""}_${lastNight?.toString() ?? ""}`;
  useChangeEffect(() => {
    if (firstNight === null) return;
    gtm.sendEvent("rt_rsv_date_filtered");
  }, dateKey);

  const facilityKey = `${[...facilitySlugs].sort().join(",")}_${evChargerAvailable}`;
  useChangeEffect(() => {
    if (facilitySlugs.size === 0 && !evChargerAvailable) return;
    gtm.sendEvent("rt_rsv_facility_filtered");
  }, facilityKey);

  const archKey = `${[...architectureTypes].sort().join(",")}_${largeOnly}`;
  useChangeEffect(() => {
    if (architectureTypes.size === 0 && !largeOnly) return;
    gtm.sendEvent("rt_rsv_arch_type_filtered");
  }, archKey);
}

/**
 * 初回マウントをスキップし、key が「実際に前回と変わった」ときだけ effect を実行する。
 * 直前の key を ref で保持して値比較するため、StrictMode の effect 二重実行や
 * 再レンダーによる effect 再実行でも多重発火しない。
 * effect は毎レンダー再生成されるが、useEffect は deps(key) の変化時に最新のクロージャを
 * 実行するため、常に最新の値で送信される。effect は意図的に deps に含めない。
 */
function useChangeEffect(effect: () => void, key: string) {
  const prevKey = useRef(key);
  useEffect(() => {
    if (prevKey.current === key) return;
    prevKey.current = key;
    effect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}
